import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  QrCode2 as QrCodeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'

const PIX_EXPIRY_SECONDS = 300 // 5 minutos

const PixPayment = ({ payment, isConfirmed, isPolling, onRefreshPix, refreshing }) => {
  const [copied, setCopied] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!payment?.pix_qr_generated_at || isConfirmed) {
      setSecondsLeft(null)
      clearInterval(intervalRef.current)
      return
    }

    const calcRemaining = () => {
      const generatedAt = new Date(payment.pix_qr_generated_at)
      const elapsed = Math.floor((Date.now() - generatedAt.getTime()) / 1000)
      return Math.max(0, PIX_EXPIRY_SECONDS - elapsed)
    }

    setSecondsLeft(calcRemaining())

    intervalRef.current = setInterval(() => {
      const remaining = calcRemaining()
      setSecondsLeft(remaining)
      if (remaining === 0) clearInterval(intervalRef.current)
    }, 1000)

    return () => clearInterval(intervalRef.current)
  }, [payment?.pix_qr_generated_at, isConfirmed])

  const handleCopyCode = () => {
    if (payment?.pix_copy_paste) {
      navigator.clipboard.writeText(payment.pix_copy_paste)
      setCopied(true)
    }
  }

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const isExpired = secondsLeft === 0

  if (!payment) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* QR Code */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
          <QrCodeIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Pague com PIX
          </Typography>
        </Box>

        {isExpired ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              QR Code expirado. Gere um novo para continuar.
            </Alert>
            <Button
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={onRefreshPix}
              disabled={refreshing}
            >
              {refreshing ? 'Gerando...' : 'Gerar novo QR Code'}
            </Button>
          </Box>
        ) : (
          <>
            {payment.pix_qr_code ? (
              <Box
                component="img"
                src={`data:image/png;base64,${payment.pix_qr_code}`}
                alt="QR Code PIX"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  margin: '0 auto',
                  display: 'block',
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'white'
                }}
              />
            ) : (
              <Alert severity="warning">QR Code não disponível</Alert>
            )}

            {secondsLeft !== null && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: secondsLeft <= 60 ? 'error.main' : 'text.secondary',
                  fontWeight: secondsLeft <= 60 ? 700 : 400
                }}
              >
                Expira em {formatCountdown(secondsLeft)}
              </Typography>
            )}
          </>
        )}

        <Typography variant="body2" color="text.secondary" mt={2}>
          Abra o app do seu banco e escaneie o QR Code
        </Typography>
      </Paper>

      {/* Código Copia e Cola */}
      {!isExpired && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Ou copie o código PIX
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            value={payment.pix_copy_paste || ''}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: '0.85rem'
              }
            }}
            sx={{ mb: 2 }}
          />

          <Button
            fullWidth
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={handleCopyCode}
            disabled={!payment.pix_copy_paste}
          >
            Copiar Código PIX
          </Button>
        </Paper>
      )}

      {/* Status do Pagamento */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {isConfirmed ? (
          <Alert severity="success" icon={<CheckIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Pagamento Confirmado!
            </Typography>
            <Typography variant="body2">
              Seu impulsionamento foi ativado com sucesso.
            </Typography>
          </Alert>
        ) : (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {isPolling && <CircularProgress size={20} />}
              <Typography variant="subtitle1" fontWeight={600}>
                {isPolling ? 'Aguardando pagamento...' : 'Pagamento pendente'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Valor: R$ {parseFloat(payment.amount).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assim que o pagamento for identificado, seu impulsionamento será ativado automaticamente.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Snackbar de código copiado */}
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopied(false)} severity="success" variant="filled">
          Código PIX copiado!
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default PixPayment
